import { CircleAlert, MailOpen } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/skeleton';
import { EmptyState } from '@/components/empty-state';
import { ApiError, errorMessage } from '@/lib/api';
import { authClient } from '@/lib/auth-client';
import { useInvitationDetails, useRespondToInvitation } from '@/lib/queries';
import { ROLE_LABEL } from '@/lib/roles';

const longDate = new Intl.DateTimeFormat('nl-NL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

/** /uitnodiging/:id — alleen ingelogd bereikbaar (app.tsx stuurt anders via inloggen hierheen terug). */
export function InvitationPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const details = useInvitationDetails(id);
  const { accept, reject } = useRespondToInvitation(id);

  async function switchAccount() {
    await authClient.signOut();
    qc.clear();
    void navigate(`/inloggen?volgende=${encodeURIComponent(`/uitnodiging/${id}`)}`, {
      replace: true,
    });
  }

  return (
    <div className="mx-auto grid max-w-xl gap-6 px-4 py-8 sm:px-6 lg:py-12">
      {details.isPending ? (
        <Skeleton className="h-48" />
      ) : details.isError ? (
        <InvitationError error={details.error} onSwitchAccount={() => void switchAccount()} />
      ) : (
        <>
          <div className="grid gap-2">
            <h1 className="text-h1 font-bold lg:text-h1-lg">
              Uitnodiging voor {details.data.invitation.organizationName}
            </h1>
            <p className="text-muted-foreground">
              {details.data.invitation.inviterName} nodigt je uit als{' '}
              {ROLE_LABEL[details.data.invitation.role].toLowerCase()}. De uitnodiging is geldig tot{' '}
              {longDate.format(new Date(details.data.invitation.expiresAt))}.
            </p>
          </div>
          {(accept.isError || reject.isError) && (
            <Alert variant="destructive">
              <CircleAlert aria-hidden="true" />
              <AlertTitle>{errorMessage(accept.error ?? reject.error)}</AlertTitle>
            </Alert>
          )}
          <div className="grid gap-2 sm:flex">
            <Button
              size="lg"
              loading={accept.isPending}
              disabled={reject.isPending}
              onClick={() =>
                accept.mutate(undefined, {
                  onSuccess: (data) => {
                    toast.success(`Je bent lid van ${data.organization.name}`);
                    void navigate('/club', { replace: true });
                  },
                })
              }
            >
              Accepteren
            </Button>
            <Button
              size="lg"
              variant="outline"
              loading={reject.isPending}
              disabled={accept.isPending}
              onClick={() =>
                reject.mutate(undefined, {
                  onSuccess: () => {
                    toast.success('Uitnodiging afgewezen');
                    void navigate('/', { replace: true });
                  },
                })
              }
            >
              Afwijzen
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function InvitationError({
  error,
  onSwitchAccount,
}: {
  error: unknown;
  onSwitchAccount: () => void;
}) {
  const otherAccount = error instanceof ApiError && error.status === 403;
  return (
    <EmptyState
      icon={MailOpen}
      title={
        otherAccount ? 'Deze uitnodiging is niet voor dit account' : 'Uitnodiging niet bruikbaar'
      }
      description={
        otherAccount
          ? `${errorMessage(error)} Log in met het account van dat e-mailadres.`
          : `${errorMessage(error)} Vraag je clubbeheerder om een nieuwe link.`
      }
      actions={
        otherAccount ? (
          <Button onClick={onSwitchAccount}>Inloggen met ander account</Button>
        ) : (
          <Button asChild variant="outline">
            <Link to="/">Naar dashboard</Link>
          </Button>
        )
      }
    />
  );
}

/** Uitleg voor wie nog niet is ingelogd; staat boven het login- of registratieformulier. */
export function InvitationLoginHint() {
  return (
    <Alert variant="info">
      <MailOpen aria-hidden="true" />
      <AlertTitle>Je bent uitgenodigd voor een club</AlertTitle>
      <AlertDescription>
        <p>Log in of maak een account met het e-mailadres waarop je de uitnodiging kreeg.</p>
      </AlertDescription>
    </Alert>
  );
}
